import {
  BlocksProvider,
  Canvas,
  LayersProvider,
  SelectorsProvider,
  StylesProvider,
  TraitsProvider,
  WithEditor,
} from "@grapesjs/react";
import {
  Box,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import LightningLanderControls from "features/lightningLanders/components/LightningLanderControls";
import LightningLandersElements from "features/lightningLanders/components/LightningLandersElements";
import LightningLandgerBuilderTopBar from "features/lightningLanders/components/LightningLandgerBuilderTopBar";
import {
  customAssetManager,
  customOnEditor,
  customStyleManagerConfig,
  MediaDrawer,
  StyledGrapesjsEditor,
} from "features/uiBuilder";
import { saveBase64ImageToMedia } from "features/uiBuilder/grapes/assetManager";
import {
  Editor,
  grapesjs,
  ProjectData,
  StorageOptions,
  Component as GrapesComponent,
} from "grapesjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import basicBlocks from "grapesjs-blocks-basic";
import customCodePlugin from "grapesjs-custom-code";
import styleBgPlugin from "grapesjs-style-bg";
import tooltipPlugin from "grapesjs-tooltip";
import componentEditorPlugin from "grapesjs-component-code-editor";
// import presetPlugin from "grapesjs-preset-webpage";
import parserPostCSS from "grapesjs-parser-postcss";
import gjsForms from "grapesjs-plugin-forms";
import LightningLandersStyles from "features/lightningLanders/components/LightningLandersStyles";
import { TControls, TTabs } from "features/lightningLanders/types";
import LightningLandersLayout from "features/lightningLanders/components/LightningLandersLayout";
import { sortByDate } from "Helpers/arrays";
import {
  ApiAIPromptStatus,
  ApiComponentType,
  ApiMediaType,
  ApiProject,
  ApiReadLanderResponse,
  ApiSimpleComponent,
} from "@incendium/api";
import { useMedias } from "Hooks/useMedia";
import Loading from "Components/Loading/Loading";
import { patchComponentTheme, saveComponent } from "features/campaigns";
import LightningLandersTraits from "features/lightningLanders/components/LightningLandersTraits";
import { useLightningLanderContext } from "features/lightningLanders/providers/LightningLanderProvider";
import LightningLandersHead from "features/lightningLanders/components/LightningLandersHead";
import AIImageBlockDialog from "features/lightningLanders/components/AIImageBlockDialog";
import AIEditImageDialog from "features/lightningLanders/components/AIEditImageDialog";
import LightningLanderForm from "features/lightningLanders/components/LightningLanderForm";
import { useLanders } from "Hooks/useLanders";
import produce from "immer";
import LightningLandersCodeEditorContainer from "features/lightningLanders/components/LightningLandersCodeEditorContainer";
import LightningLanderSelectorManager from "features/lightningLanders/components/LightningLanderSelectorManager";
import LightningLandersAIEdit from "features/lightningLanders/components/LightningLandersAIEdit";
import { customJsScript } from "features/lightningLanders/constants";
import { StylesUpdateProvider } from "features/lightningLanders/providers/StylesUpdateProvider";
import LightningLanderThemeEditor from "features/lightningLanders/components/LightningLanderThemeEditor";
import { useLightningLanderThemeSettings } from "features/lightningLanders/providers/LightningLanderThemeProvider";
import { registerLightningLandersCustomBlocks } from "features/lightningLanders/components/LightningLandersCustomBlocks";
import { usePrevious } from "react-use";
import { isEqual } from "Helpers/objects";
import {
  addGridStyles,
  generateThemeStyles,
  addOrUpdateScriptTag,
  getCanvasDocument,
} from "features/lightningLanders/utils";
import { persistLanderComponent } from "features/lightningLanders/utils/persistLanderComponent";
import LightningLanderComponentsSidebar, {
  DYNAMIC_PRODUCT_GRID_TYPE,
} from "features/lightningLanders/components/LightningLanderComponentsSidebar";
import { useUpdateEffect } from "react-use";
import { AnimatePresence, motion } from "motion/react";
import { useLanderBlocks } from "features/lightningLanders/hooks/useLanderBlocks";
import { usePollPendingComponents } from "features/campaigns/hooks/usePollPendingComponents";
import { useNotification } from "Hooks";
import NameAndDescriptionDialog from "Components/NameAndDescriptionDialog/NameAndDescriptionDialog";
import LightningLanderVariableMapping from "features/lightningLanders/components/LightningLanderVariableMapping";
import DynamicProductGridModal from "features/lightningLanders/components/DynamicProductGridModal";
import type { LightningLanderComponentsSidebarProps } from "features/lightningLanders/components/LightningLanderComponentsSidebar";
import {
  GRID_CONFIG_ATTR,
  type ProductGridConfig,
} from "features/lightningLanders/utils/dynamicBlockGrid";

const DEFAULT_COMPONENT_CANVAS_WIDTH = 400;

const LANDER_CONTROLS: TControls[] = [
  "elements",
  "layout",
  "head",
  "media",
  "settings",
  "theme",
  "components",
];

function getControlFromSearch(search: string): TControls | null {
  const value = new URLSearchParams(search).get("control");
  return LANDER_CONTROLS.includes(value as TControls)
    ? (value as TControls)
    : null;
}

interface ILightningLandersBuilderProps {
  saveToken: string | null;
  project: ApiProject;
  onSaved: (v: ApiSimpleComponent) => void;
  onLanderSaved?: (v: ApiReadLanderResponse) => void;
  customSaveFunction?: (
    component: ApiSimpleComponent,
    token?: string,
  ) => Promise<{ component?: ApiSimpleComponent }>;
  /** When true, show width slider instead of device switcher (for landing components). */
  componentMode?: boolean;
}

function LightningLandersBuilder({
  project,
  onSaved,
  saveToken,
  onLanderSaved: onLanderSavedProp,
  customSaveFunction,
  componentMode = false,
}: ILightningLandersBuilderProps) {
  const { component, setComponent } = useLightningLanderContext();
  const isPromptPending =
    component?.prompt?.status === ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING;
  const location = useLocation();

  usePollPendingComponents({
    projectId: project?.id as number | undefined,
    components: component?.id ? component : null,
    onStatusChange: (updated) => {
      setComponent(updated);
      if (editor && updated?.html != null) {
        try {
          editor.setComponents(updated.html ?? "");
          if (updated.css) editor.addStyle(updated.css);
        } catch (e) {
          console.error("Failed to load AI-generated content into editor", e);
        }
      }
    },
    interval: 4000,
    enabled: !!project?.id && !!component?.id && isPromptPending,
  });
  const theme = useTheme();
  const { theme: customTheme, setTheme } = useLightningLanderThemeSettings();
  const customThemeRef = useRef(customTheme);
  const prevCustomTheme = usePrevious(customTheme);
  const { medias, setMedias } = useMedias();
  const [editor, setEditor] = useState<Editor | null>(null);
  const [control, setControl] = useState<TControls>(
    () => getControlFromSearch(location.search) ?? "elements",
  );
  const [tab, setTab] = useState<TTabs>("styles");
  const [editorLoaded, seteditorLoaded] = useState(false);
  const [componentCanvasWidth, setComponentCanvasWidth] = useState(
    DEFAULT_COMPONENT_CANVAS_WIDTH,
  );
  const latestComponentRef = useRef(component);
  const [mediaOpen, setMediaOpen] = useState(false);
  const { landers, setLanders } = useLanders();
  const [componentSelected, setComponentSelected] = useState(false);
  const [selectedComponent, setSelectedComponent] =
    useState<GrapesComponent | null>(null);
  const { blocks: landerBlocks, setBlocks } = useLanderBlocks();

  useEffect(() => {
    const fromSearch = getControlFromSearch(location.search);
    if (fromSearch) {
      setControl(fromSearch);
    }
  }, [location.search]);

  usePollPendingComponents({
    projectId: project?.id as number | undefined,
    components: landerBlocks,
    setComponents: setBlocks,
    interval: 4000,
    enabled: !componentMode && !!project?.id && landerBlocks.length > 0,
  });
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [saveBlockDialogOpen, setSaveBlockDialogOpen] = useState(false);
  const [selectedComponentForBlock, setSelectedComponentForBlock] =
    useState<any>(null);
  const onSaveAsBlockRef = useRef<(editor: Editor) => void>(() => {});
  const [aiImageDialogOpen, setAiImageDialogOpen] = useState(false);
  const [pendingAiImageComponent, setPendingAiImageComponent] =
    useState<GrapesComponent | null>(null);
  const [editImageDialogOpen, setEditImageDialogOpen] = useState(false);
  const [selectedImageForEdit, setSelectedImageForEdit] =
    useState<GrapesComponent | null>(null);
  const [editImageUrl, setEditImageUrl] = useState<string>("");
  const [aiEditDialogOpen, setAiEditDialogOpen] = useState(false);
  const [blockDropLoading, setBlockDropLoading] = useState(false);
  const setBlockDropLoadingRef = useRef(setBlockDropLoading);
  const [productGridModalOpen, setProductGridModalOpen] = useState(false);
  const [productGridInitialConfig, setProductGridInitialConfig] =
    useState<ProductGridConfig | null>(null);
  const productGridDropTargetRef = useRef<{
    targetComponent: any;
    insertionIndex: number;
  } | null>(null);
  const productGridEditComponentRef = useRef<any>(null);
  const setProductGridModalOpenRef = useRef(setProductGridModalOpen);
  const setProductGridInitialConfigRef = useRef(setProductGridInitialConfig);

  // Keep setBlockDropLoadingRef in sync so drop handler can trigger loading
  useEffect(() => {
    setBlockDropLoadingRef.current = setBlockDropLoading;
  }, []);
  useEffect(() => {
    setProductGridModalOpenRef.current = setProductGridModalOpen;
  }, [productGridModalOpen]);
  useEffect(() => {
    setProductGridInitialConfigRef.current = setProductGridInitialConfig;
  }, [setProductGridInitialConfig]);

  // Keep customThemeRef always up to date
  useEffect(() => {
    customThemeRef.current = customTheme;
  }, [customTheme]);

  useUpdateEffect(() => {
    latestComponentRef.current = component;
  }, [component]);

  const persistEditorComponent = useCallback(async () => {
    if (!editor) {
      throw new Error("Editor is not ready");
    }
    if (
      latestComponentRef.current?.prompt?.status ===
      ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING
    ) {
      throw new Error("Cannot save while AI is processing");
    }

    const saved = await persistLanderComponent({
      editor,
      projectData: editor.getProjectData(),
      component: latestComponentRef.current,
      projectId: project?.id as number,
      theme: customThemeRef.current,
      saveToken: saveToken || undefined,
      includeTheme: !customSaveFunction,
      customSaveFunction,
    });
    onSaved(saved);
  }, [editor, project?.id, saveToken, onSaved, customSaveFunction]);

  const onSaveComponent = useCallback(
    (editorInstance: Editor) =>
      async (data: ProjectData, _options: StorageOptions) => {
        if (
          latestComponentRef.current?.prompt?.status ===
          ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING
        ) {
          return;
        }

        const saved = await persistLanderComponent({
          editor: editorInstance,
          projectData: data,
          component: latestComponentRef.current,
          projectId: project?.id as number,
          theme: customThemeRef.current,
          saveToken: saveToken || undefined,
          includeTheme: !customSaveFunction,
          customSaveFunction,
        });
        onSaved(saved);
      },
    [project?.id, saveToken, onSaved, customSaveFunction],
  );

  const onEditor = useCallback(
    (editor: Editor) => {
      editor = customOnEditor(editor, component!, onSaveComponent);
      registerLightningLandersCustomBlocks(editor);

      editor.on("component:add", (component: GrapesComponent) => {
        const attrs = component.get("attributes") || {};
        if (attrs["data-ai-image"]) {
          setPendingAiImageComponent(component);
          setAiImageDialogOpen(true);
        }
      });

      editor.Commands.add("open-settings", {
        run: function (editor) {
          // Trigger the settings tab to open
          setTab("settings");
        },
      });

      editor.Commands.add("open-ai-edit", {
        run: () => {
          const selected = editor.getSelected();
          if (!selected) {
            showErrorNotification("Please select a component to edit with AI.");
            return;
          }
          setAiEditDialogOpen(true);
        },
      });

      editor.Commands.add("save-as-block", {
        run: function (editor: Editor) {
          onSaveAsBlockRef.current(editor);
        },
      });

      editor.Commands.add("open-dynamic-grid-settings", {
        run: function () {
          let comp: any = editor.getSelected();
          while (comp) {
            const el = comp.getEl?.();
            const attr = el?.getAttribute?.(GRID_CONFIG_ATTR);
            if (attr) {
              try {
                const config = JSON.parse(
                  decodeURIComponent(attr),
                ) as ProductGridConfig;
                productGridEditComponentRef.current = comp;
                setProductGridInitialConfigRef.current(config);
                setProductGridModalOpenRef.current(true);
                return;
              } catch {
                // ignore parse errors
              }
            }
            comp = comp.parent?.();
          }
        },
      });

      editor.Commands.add("open-ai-edit-image", {
        run: async () => {
          const selected = editor.getSelected();
          if (!selected || selected.get("type") !== "image") {
            showErrorNotification(
              "Select an image or AI image to edit with AI.",
            );
            return;
          }
          const src = selected.get("attributes")?.src;
          if (!src || typeof src !== "string") {
            showErrorNotification("Image has no source.");
            return;
          }
          let url: string;
          if (src.startsWith("data:")) {
            try {
              url = await saveBase64ImageToMedia(
                project.client!,
                project,
                setMedias,
                src,
              );
            } catch (e) {
              showErrorNotification(
                e instanceof Error
                  ? e.message
                  : "Failed to prepare image for edit",
              );
              return;
            }
          } else {
            url = src;
          }
          setSelectedImageForEdit(selected);
          setEditImageUrl(url);
          setEditImageDialogOpen(true);
        },
      });

      // this is a workaround to open the assets dialog when the edit-assets command is run, for some reason open asset did not work, but double clicking does so im just going to dispatch a double click event on the component
      editor.Commands.add("edit-assets", {
        run: function () {
          const selected = editor.getSelected();
          if (!selected || selected.get("type") !== "image") {
            return;
          }
          const el = selected.getEl();
          if (!el) return;
          el.dispatchEvent(
            new MouseEvent("dblclick", {
              bubbles: true,
              cancelable: true,
              view: el.ownerDocument?.defaultView ?? window,
              detail: 2,
            }),
          );
        },
      });

      // Add drag and drop handling for components
      editor.on("load", () => {
        addGridStyles(editor);
        const canvasDoc = getCanvasDocument(editor);
        if (canvasDoc && !canvasDoc.getElementById("hide-default-toolbar")) {
          const style = canvasDoc.createElement("style");
          style.id = "hide-default-toolbar";
          style.textContent =
            '.gjs-toolbar, .gjs-cv-toolbar, [class*="gjs-toolbar"] { display: none !important; }';
          canvasDoc.head.appendChild(style);
        }

        // Check if we have grapeJs data, if not, load from HTML
        const hasGrapeJsData = component?.grapeJs && component.grapeJs !== "";
        const editorIsEmpty =
          editor.getHtml() === "" || editor.getHtml() === "<body></body>";

        if (
          !hasGrapeJsData &&
          editorIsEmpty &&
          component?.aiHtml &&
          component.aiHtml !== ""
        ) {
          const htmlStr = component.aiHtml || "";
          const innerHtml = htmlStr
            .replace(/<\/?(?:html|head|body)[^>]*>/gi, "")
            .trim();
          editor.setComponents(innerHtml);

          editor.setStyle(
            component.aiCss ||
              editor.getCss({
                keepUnusedStyles: true,
                avoidProtected: true,
              }),
          );

          editor.select();
        }

        // Restore custom JS from component to canvas
        if (component?.js) {
          const canvasDoc = getCanvasDocument(editor);
          if (canvasDoc) {
            addOrUpdateScriptTag(canvasDoc, customJsScript, component.js);
          }
        }

        seteditorLoaded(true);

        // Helper function to find insertion index based on drop position
        const findInsertionIndex = (targetComponent: any, dropY: number) => {
          const children = targetComponent.components();
          if (children.length === 0) {
            return 0; // Insert at the beginning if no children
          }

          // Get the target component's element and its position
          const targetEl = targetComponent.getEl();
          const targetRect = targetEl.getBoundingClientRect();

          // Calculate relative drop position within the target component
          const relativeDropY = dropY - targetRect.top;

          // Check each child to find the insertion point
          for (let i = 0; i < children.length; i++) {
            const child = children.at(i);
            const childEl = child.getEl();
            if (childEl) {
              let childRect;
              try {
                childRect = childEl.getBoundingClientRect();
              } catch (e) {
                // If getBoundingClientRect fails, try to get position from parent
                const parentEl = childEl.parentElement;
                childRect = parentEl
                  ? parentEl.getBoundingClientRect()
                  : targetRect;
              }
              const childTop = childRect.top - targetRect.top;

              // If drop is above the middle of this child, insert before it
              if (relativeDropY < childTop + childRect.height / 2) {
                return i;
              }
            }
          }

          // If drop is below all children, insert at the end
          return children.length;
        };

        // Handle component drops (capture: true so we run before grid/children and get dataTransfer)
        editor.Canvas.getBody().addEventListener(
          "dragover",
          (e) => {
            e.preventDefault();
            if (e.dataTransfer) {
              e.dataTransfer.dropEffect = "copy";
            }
          },
          true,
        );

        editor.Canvas.getBody().addEventListener(
          "drop",
          (e) => {
            if (!e.dataTransfer) return;

            let rawData: string;
            try {
              rawData = e.dataTransfer.getData("application/json");
            } catch {
              return;
            }
            if (!rawData) return;

            e.preventDefault();

            let componentData: {
              type?: string;
              componentId?: number;
              html?: string;
              css?: string;
              js?: string;
              grapeJs?: string;
            };
            try {
              componentData = JSON.parse(rawData);
            } catch {
              return;
            }

            // Dynamic product grid
            if (componentData.type === DYNAMIC_PRODUCT_GRID_TYPE) {
              e.stopPropagation();
              const dropTarget = e.target as HTMLElement;
              const clientY = e.clientY;
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  try {
                    const wrapper = editor.getWrapper();
                    const targetComponent = wrapper?.find(
                      `#${dropTarget.id}`,
                    )[0];
                    const insertionIndex = targetComponent
                      ? findInsertionIndex(targetComponent, clientY)
                      : 0;
                    productGridDropTargetRef.current = {
                      targetComponent: targetComponent ?? null,
                      insertionIndex,
                    };
                    setProductGridModalOpenRef.current(true);
                  } catch (err) {
                    console.error("Dynamic product grid drop:", err);
                  }
                });
              });
              return;
            }

            if (componentData.type !== "component") return;

            setBlockDropLoadingRef.current(true);
            // Defer work so React can paint the loading overlay first
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                try {
                  const wrapper = editor.getWrapper();
                  const targetComponent = wrapper?.find(
                    `#${(e.target as HTMLElement).id}`,
                  )[0];

                  // Prefer component JSON over HTML: skips GrapesJS HTML parsing and is much faster on large docs
                  // grapeJs can be: (1) { components: [...] } from "Save as block", or (2) full project { pages: [{ frames: [{ component: { components: [...] } }] }] }
                  const componentsToAdd = componentData.grapeJs
                    ? (() => {
                        try {
                          const data = JSON.parse(componentData.grapeJs!);
                          const fromRoot = data?.components;
                          const fromPages =
                            data?.pages?.[0]?.frames?.[0]?.component
                              ?.components;
                          const list =
                            Array.isArray(fromRoot) && fromRoot.length > 0
                              ? fromRoot
                              : Array.isArray(fromPages) && fromPages.length > 0
                                ? fromPages
                                : null;
                          return list ?? null;
                        } catch {
                          return null;
                        }
                      })()
                    : null;

                  if (componentsToAdd && componentsToAdd.length > 0) {
                    if (targetComponent) {
                      const insertionIndex = findInsertionIndex(
                        targetComponent,
                        e.clientY,
                      );
                      componentsToAdd.forEach((comp: any, i: number) => {
                        targetComponent.append(comp, {
                          at: insertionIndex + i,
                        });
                      });
                    } else {
                      editor.addComponents(componentsToAdd);
                    }
                  } else {
                    const htmlStr = componentData.html || "";
                    const innerHtml = htmlStr
                      .replace(/<\/?(?:html|head|body)[^>]*>/gi, "")
                      .trim();
                    if (targetComponent) {
                      const insertionIndex = findInsertionIndex(
                        targetComponent,
                        e.clientY,
                      );
                      targetComponent.append(innerHtml, { at: insertionIndex });
                    } else {
                      editor.addComponents(innerHtml);
                    }
                  }

                  // Add block CSS outside undo so undo only removes components; rules stay and re-add gets styles
                  if (componentData.css) {
                    editor.UndoManager.skip(() => {
                      editor.Css.addRules(componentData.css!);
                    });
                  }

                  if (componentData.js) {
                    const canvasDoc = getCanvasDocument(editor);
                    if (canvasDoc) {
                      const existingScript = canvasDoc.querySelector(
                        `script[${customJsScript}]`,
                      );
                      const existingJs = existingScript
                        ? existingScript.textContent || ""
                        : "";
                      const newJs = existingJs
                        ? `${existingJs}\n\n${componentData.js}`
                        : componentData.js;
                      addOrUpdateScriptTag(canvasDoc, customJsScript, newJs);
                    }
                  }
                } catch (error) {
                  console.error("Error dropping component:", error);
                } finally {
                  setBlockDropLoadingRef.current(false);
                }
              });
            });
          },
          true,
        );

        // Auto-save is disabled - user must manually save via the Save button
        editor.StorageManager.setAutosave(false);
      });

      setEditor(editor);
    },
    [component, onSaveComponent, showErrorNotification, project, setMedias],
  );

  // Sync selection state with GrapesJS using component:select (single source of truth)
  useEffect(() => {
    if (!editor) return;
    const onSelect = () => {
      const selected = editor.getSelected();
      setComponentSelected(!!selected);
      setSelectedComponent(selected ?? null);
    };
    editor.on("component:select", onSelect);
    onSelect(); // initial sync
    return () => {
      editor.off("component:select", onSelect);
    };
  }, [editor]);

  const images = useMemo(
    () =>
      sortByDate(
        (medias || []).filter(
          (m) => m.files && m.type === ApiMediaType.IMAGE_MEDIA,
        ),
        "createdAt",
      ).map((im) => (im.files || [])[0].fullPath || ""),
    [medias],
  );

  useEffect(() => {
    if (!editor) {
      return;
    }
    editor.AssetManager.getAll().reset([]);
    images.reverse().forEach((img) => {
      if (!editor.AssetManager.get(img)) {
        editor.AssetManager.add(img);
      }
    });
  }, [editor, images]);

  useEffect(() => {
    const doc =
      editor && editorLoaded && component ? getCanvasDocument(editor) : null;
    if (!doc) return;
    let head = doc.head.innerHTML;
    if (
      !head.includes("<!-- start custom -->") ||
      !head.includes("<!-- end custom -->")
    ) {
      head += "<!-- start custom --><!-- end custom -->";
    }

    const pattern = /<!-- start custom -->([\s\S]*?)<!-- end custom -->/;
    const result = head.replace(
      pattern,
      `\n<!-- start custom -->\n${component!.head}\n<!-- end custom -->\n`,
    );

    doc.head.innerHTML = result;
  }, [component, editorLoaded, editor]);

  const onLanderSaved = useCallback(
    (res: ApiReadLanderResponse) => {
      setLanders(
        produce(landers, (draft) => {
          const idx = draft.findIndex((d) => d.id === res.id);
          if (idx >= 0) {
            draft[idx] = res;
          } else {
            draft.push(res);
          }
        }),
      );
      // Also call the parent callback if provided
      if (onLanderSavedProp) {
        onLanderSavedProp(res);
      }
    },
    [landers, setLanders, onLanderSavedProp],
  );

  const onSaveAsBlock = useCallback(
    (editor: Editor) => {
      const selected = editor.getSelected();
      if (!selected) {
        showErrorNotification("Please select a component to save as block");
        return;
      }

      // Store the selected component and open the dialog
      setSelectedComponentForBlock(selected);
      setSaveBlockDialogOpen(true);
    },
    [showErrorNotification],
  );

  useEffect(() => {
    onSaveAsBlockRef.current = onSaveAsBlock;
  }, [onSaveAsBlock]);

  const handleProductGridInsert = useCallback(
    (html: string, css: string) => {
      if (!editor) return;
      const innerHtml = html
        .replace(/<\/?(?:html|head|body)[^>]*>/gi, "")
        .trim();

      const editComp = productGridEditComponentRef.current;
      if (editComp) {
        const parent = editComp.parent?.();
        const index = editComp.index?.() ?? 0;
        if (parent && typeof parent.append === "function") {
          editComp.remove();
          try {
            parent.append(innerHtml, { at: index });
          } catch {
            editor.addComponents(html);
          }
        } else {
          editComp.remove();
          editor.addComponents(html);
        }
        productGridEditComponentRef.current = null;
        setProductGridInitialConfig(null);
      } else {
        const ref = productGridDropTargetRef.current;
        const appended = (() => {
          const target = ref?.targetComponent;
          if (target && typeof target.append === "function") {
            try {
              target.append(innerHtml, { at: ref!.insertionIndex });
              return true;
            } catch {
              return false;
            }
          }
          return false;
        })();
        if (!appended) {
          editor.addComponents(html);
        }
        productGridDropTargetRef.current = null;
      }

      editor.UndoManager.skip(() => {
        editor.Css.addRules(css);
      });
    },
    [editor],
  );

  const handleSaveBlock = useCallback(
    async (name: string, description: string) => {
      try {
        if (!selectedComponentForBlock) {
          return;
        }

        // Get the component's HTML and CSS
        const componentHtml = selectedComponentForBlock.toHTML();
        const componentCss = editor?.getCss({
          component: selectedComponentForBlock,
          keepUnusedStyles: true,
        });

        // Create a new block component
        const res = await saveComponent(project?.id as number, {
          name,
          description,
          type: ApiComponentType.LANDING_BLOCK,
          html: componentHtml,
          css: componentCss,
          grapeJs: JSON.stringify({
            components: [selectedComponentForBlock.toJSON()],
          }),
          theme: JSON.stringify(customThemeRef.current),
        });

        // Add to blocks list
        setBlocks((blocks) =>
          produce(blocks, (draft) => {
            draft.push(res.component!);
          }),
        );

        showSuccessNotification(
          `Block "${res.component?.name}" saved successfully!`,
        );
      } catch (error) {
        console.error("Error saving block:", error);
        showErrorNotification("Failed to save block. Please try again.");
      }
    },
    [
      selectedComponentForBlock,
      editor,
      project,
      setBlocks,
      showSuccessNotification,
      showErrorNotification,
    ],
  );

  // Single theme-injection effect: theme vars + builder button styles into canvas; patch theme when changed
  useEffect(() => {
    const themeStyles = generateThemeStyles(customTheme);
    const builderStyles = `
      ${themeStyles}
      .inc-button-primary { background: var(--primary-color); }
      .inc-button-secondary { background: var(--secondary-color); }
    `;

    const doc = editor && editorLoaded ? getCanvasDocument(editor) : null;
    if (doc) {
      let styleTag = doc.getElementById("theme-vars");
      if (styleTag) styleTag.remove();
      styleTag = doc.createElement("style");
      styleTag.id = "theme-vars";
      styleTag.innerHTML = builderStyles;
      doc.head.appendChild(styleTag);
    }

    if (
      !isEqual(customTheme, prevCustomTheme) &&
      !customSaveFunction &&
      !isPromptPending
    ) {
      patchComponentTheme(
        project.id as number,
        component.id as number,
        customTheme,
        saveToken || undefined,
      );
    }
  }, [
    editor,
    editorLoaded,
    customTheme,
    prevCustomTheme,
    project.id,
    saveToken,
    component.id,
    customSaveFunction,
    isPromptPending,
  ]);

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      {(!editorLoaded || blockDropLoading) && (
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            background: blockDropLoading
              ? `rgba(255, 255, 255, 0.5)`
              : theme.palette.background.default,
            zIndex: 999,
            boxShadow: `0 0 0px 10px ${theme.palette.background.default}`,
          }}
        >
          <Loading />
        </Box>
      )}

      {editorLoaded && !blockDropLoading && isPromptPending && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 998,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255, 255, 255, 0.7)",
            gap: 2,
          }}
        >
          <Box>
            <Loading size={60} />
          </Box>
          <Typography variant="h6" color="text.primary">
            AI is processing your component
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This can take a few minutes to complete. We shall reload the page
            when it is ready.
          </Typography>
        </Box>
      )}

      <StyledGrapesjsEditor
        custom
        // Pass the core GrapesJS library to the wrapper (required).
        // You can also pass the CDN url (eg. "https://unpkg.com/grapesjs")
        grapesjs={grapesjs}
        // Load the GrapesJS CSS file asynchronously from URL.
        // This is an optional prop, you can always import the CSS directly in your JS if you wish.
        grapesjsCss="https://unpkg.com/grapesjs/dist/css/grapes.min.css"
        plugins={[
          parserPostCSS,
          // suggestClasses, // this plugin is cuasing performance issues when pasting html
          (editor) =>
            basicBlocks(editor, {
              blocks: ["text", "link", "image", "video"],
            }),
          customCodePlugin,
          styleBgPlugin,
          // Use gjsForms with block options for button and form
          (editor) =>
            gjsForms(editor, {
              block: (blockId: string) => {
                if (blockId === "button") {
                  return {
                    content: {
                      type: "button",
                      attributes: { class: "inc-button inc-button-primary" },
                    },
                  };
                }
                if (blockId === "form") {
                  // Return empty form - styles are handled by form component defaults
                  return {
                    content: {
                      type: "form",
                      components: [],
                    },
                  };
                }
                return {};
              },
            }),
          tooltipPlugin,
          (editor) =>
            componentEditorPlugin(editor, {
              // mount into your container:
              appendTo: "#gjs-code-editor-container",
              theme: "material",
              editJs: true,
              htmlBtnText: "Apply HTML",
              cssBtnText: "Apply CSS",
            }),
        ]}
        // GrapesJS init options
        options={{
          // @ts-ignore, ignore as for some reason interface does not have it.
          allowScripts: true,
          height: "100%",
          showOffsets: true,
          showOffsetsSelected: true,
          selectorManager: {
            componentFirst: true,
          },
          storageManager: {
            type: "custom",
            autoload: true,
            autosave: false,
          },
          assetManager: customAssetManager(
            project.client!,
            project,
            [],
            setMedias,
          ),
          styleManager: customStyleManagerConfig(),
          canvasCss: `
        [data-gjs-type="wrapper"]:not(:empty):after {
          content: "Drag and drop components here";
          color: #000;
          text-align: center;
          display: block;
          padding: 15px;
          background-color: #ddd;
          opacity: 0.3;
        }
    [data-gjs-type="container"]:empty:before, .inc-container:empty:before {
    background-color: #ddd;
    color: #000;
    font-size: 16px;
    font-weight: bold;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 50px;
    padding: 0 10px;
    opacity: 0.3;
    border-radius: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    content: "Container";
}
    [data-gjs-type="box"]:empty {
     height: 50px;
    }
    [data-gjs-type="box"]:not(:empty) {
     transition: padding 0.1s;
    }
    [data-gjs-type="box"]:not(:empty):hover {
      padding: 4px;
    }
    [data-gjs-type="grid"] {
     transition: padding 0.1s;
    }
    [data-gjs-type="grid"]:hover {
      padding-top: 4px;
      padding-bottom: 4px;
    }
            .inc-md-col-1.locked { width: 8.33%!important; min-height: 50px; flex: 0 0 8.33%!important; }
            .inc-md-col-2.locked { width: 16.66%!important; min-height: 50px; flex: 0 0 16.66%!important; }
            .inc-md-col-3.locked { width: 25%!important; min-height : 50px; flex: 0 0 25%!important; }
            .inc-md-col-4.locked { width: 33.33%!important; min-height: 50px; flex: 0 0 33.33%!important; }
            .inc-md-col-5.locked { width: 41.66%!important; min-height: 50px; flex: 0 0 41.66%!important; }
            .inc-md-col-6.locked { width: 50%!important; min-height: 50px; flex: 0 0 50%!important; }
            .inc-md-col-7.locked { width: 58.33%!important; min-height: 50px; flex: 0 0 58.33%!important; }
            .inc-md-col-8.locked { width: 66.66%!important; min-height: 50px; flex: 0 0 66.66%!important; }
            .inc-md-col-9.locked { width: 75%!important; min-height: 50px; flex: 0 0 75%!important; }
            .inc-md-col-10.locked { width: 83.33%!important; min-height: 50px; flex: 0 0 83.33%!important; }
            .inc-md-col-11.locked { width: 91.66%!important; min-height: 50px; flex: 0 0 91.66%!important; }
            .inc-md-col-12.locked { width: 100%!important; min-height: 50px; flex: 0 0 100%!important; }

`,
        }}
        onEditor={onEditor}
      >
        <StylesUpdateProvider>
          <AnimatePresence>
            <Box sx={{ backgroundColor: "#edf1fe", height: "100vh" }}>
              <WithEditor>
                <LightningLandgerBuilderTopBar
                  componentMode={componentMode}
                  canvasWidth={componentCanvasWidth}
                  onCanvasWidthChange={setComponentCanvasWidth}
                  onPersistComponent={persistEditorComponent}
                />
              </WithEditor>
              <Grid container sx={{ height: "100vh" }} pt={6}>
                {/* Main Content */}
                <Grid container item xs={12} sx={{ height: "100%" }}>
                  {/* Left Column */}
                  <Grid item>
                    <LightningLanderControls
                      control={control}
                      setControl={(c) => {
                        setMediaOpen(c === "media");
                        setControl(c);
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    sx={{
                      width: ["head", "settings"].includes(control) ? 500 : 300,
                      bgcolor: "#edf1fe",
                      transition: "width 0.3s",
                      willChange: "width",
                      height: `calc(100vh - 48px)`,
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                  >
                    {control === "layout" ? (
                      <LayersProvider>
                        {(props) => <LightningLandersLayout {...props} />}
                      </LayersProvider>
                    ) : control === "head" ? (
                      <LightningLandersHead
                        project={project}
                        saveToken={saveToken}
                      />
                    ) : control === "settings" ? (
                      <LightningLanderForm
                        project={project}
                        onSaved={onLanderSaved}
                      />
                    ) : control === "theme" ? (
                      <LightningLanderThemeEditor
                        theme={customTheme}
                        setTheme={setTheme}
                      />
                    ) : control === "components" ? (
                      <LightningLanderComponentsSidebar
                        variant={
                          component?.type === ApiComponentType.LANDING_BLOCK
                            ? "components"
                            : "blocks"
                        }
                        {...({
                          onOpenProductGridModal: () => {
                            productGridDropTargetRef.current = null;
                            setProductGridModalOpen(true);
                          },
                        } as LightningLanderComponentsSidebarProps)}
                      />
                    ) : (
                      <BlocksProvider>
                        {(props) => <LightningLandersElements {...props} />}
                      </BlocksProvider>
                    )}
                  </Grid>

                  <Grid
                    item
                    sx={{
                      flexGrow: 1,
                      bgcolor: theme.palette.background.default,
                    }}
                  >
                    <Box
                      px={8}
                      height="100%"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                      }}
                    >
                      <WithEditor>
                        <LightningLandersAIEdit
                          open={aiEditDialogOpen}
                          onClose={() => setAiEditDialogOpen(false)}
                          projectId={project.id as number}
                          selectedComponent={selectedComponent}
                          onApplied={() => setAiEditDialogOpen(false)}
                        />
                      </WithEditor>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          ...(componentMode
                            ? {
                                maxWidth: componentCanvasWidth,
                                margin: "0 auto",
                                width: "100%",
                              }
                            : {}),
                        }}
                      >
                        <Canvas />
                      </Box>
                    </Box>
                  </Grid>
                  <SelectorsProvider>
                    {(selectorProps) => (
                      <StylesProvider>
                        {(stylesProps) => (
                          <>
                            <Grid
                              item
                              sx={{
                                width: tab === "code" ? 450 : 330,
                                height: "100%",
                                bgcolor: "#edf1fe",
                                overflowY: "auto",
                                overflowX: "hidden",
                                borderLeft: `1px solid ${theme.palette.divider2}`,
                                transition: "width 0.2s",
                                willChange: "width",
                              }}
                            >
                              <Tabs
                                variant="fullWidth"
                                value={tab}
                                onChange={(_, value) => {
                                  if (!editor) {
                                    setTab(value);
                                    return;
                                  }

                                  editor.Commands.stop("open-code");
                                  if (value === "code") {
                                    editor.runCommand("open-code");
                                  }

                                  if (tab === "code" && value === "styles") {
                                    // force the Style Manager to reload on the selected component
                                    const sel = editor.getSelected();
                                    if (sel) {
                                      editor.selectToggle(sel);
                                      editor.select(sel);
                                    }
                                  }
                                  setTab(value);
                                }}
                              >
                                <Tab
                                  sx={{ minWidth: 10 }}
                                  label="Styles"
                                  value={"styles"}
                                />
                                <Tab
                                  sx={{ minWidth: 10 }}
                                  label="Settings"
                                  value={"settings"}
                                />
                                <Tab
                                  sx={{ minWidth: 10 }}
                                  label="Code"
                                  value={"code"}
                                />
                              </Tabs>
                              {componentSelected && (
                                <Box
                                  component={motion.div}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  sx={{
                                    display:
                                      tab === "styles" ? "block" : "none",
                                    opacity: tab === "styles" ? 1 : 0,
                                    height: tab === "styles" ? "auto" : 0,
                                  }}
                                >
                                  <LightningLanderSelectorManager
                                    {...selectorProps}
                                  />
                                  <LightningLandersStyles {...stylesProps} />
                                </Box>
                              )}
                              {tab === "settings" && (
                                <>
                                  <TraitsProvider>
                                    {(props) => (
                                      <LightningLandersTraits {...props} />
                                    )}
                                  </TraitsProvider>
                                  {component &&
                                    [
                                      ApiComponentType.LANDING_BLOCK,
                                      ApiComponentType.LANDING_COMPONENT,
                                    ].includes(component.type!) && (
                                      <>
                                        <Divider />
                                        <LightningLanderVariableMapping />
                                      </>
                                    )}
                                </>
                              )}

                              <LightningLandersCodeEditorContainer
                                id="gjs-code-editor-container"
                                visible={tab === "code"}
                              />
                            </Grid>
                          </>
                        )}
                      </StylesProvider>
                    )}
                  </SelectorsProvider>
                </Grid>
              </Grid>
            </Box>
          </AnimatePresence>
        </StylesUpdateProvider>
      </StyledGrapesjsEditor>

      <MediaDrawer project={project} open={mediaOpen} setOpen={setMediaOpen} />

      <DynamicProductGridModal
        open={productGridModalOpen}
        onClose={() => {
          productGridDropTargetRef.current = null;
          productGridEditComponentRef.current = null;
          setProductGridInitialConfig(null);
          setProductGridModalOpen(false);
          editor?.select();
        }}
        onInsert={handleProductGridInsert}
        editor={editor}
        initialConfig={productGridInitialConfig}
      />

      <NameAndDescriptionDialog
        open={saveBlockDialogOpen}
        setOpen={setSaveBlockDialogOpen}
        title="Save as Block"
        onSaved={handleSaveBlock}
      />
      <AIImageBlockDialog
        open={aiImageDialogOpen}
        onClose={() => {
          setAiImageDialogOpen(false);
          setPendingAiImageComponent(null);
        }}
        onGenerated={async (dataUrl) => {
          try {
            const url = await saveBase64ImageToMedia(
              project.client!,
              project,
              setMedias,
              dataUrl,
            );
            editor?.AssetManager?.add(url);
            pendingAiImageComponent?.addAttributes({ src: url });
            setAiImageDialogOpen(false);
            setPendingAiImageComponent(null);
          } catch (e) {
            showErrorNotification(
              e instanceof Error ? e.message : "Failed to save image",
            );
          }
        }}
      />
      <AIEditImageDialog
        open={editImageDialogOpen}
        onClose={() => {
          setEditImageDialogOpen(false);
          setSelectedImageForEdit(null);
          setEditImageUrl("");
        }}
        imageUrl={editImageUrl}
        onEdited={async (dataUrl) => {
          try {
            const url = await saveBase64ImageToMedia(
              project.client!,
              project,
              setMedias,
              dataUrl,
            );
            editor?.AssetManager?.add(url);
            selectedImageForEdit?.addAttributes({ src: url });
            setEditImageDialogOpen(false);
            setSelectedImageForEdit(null);
            setEditImageUrl("");
          } catch (e) {
            showErrorNotification(
              e instanceof Error ? e.message : "Failed to save edited image",
            );
          }
        }}
      />
    </Box>
  );
}

export default LightningLandersBuilder;
